import { useCallback, useEffect, useRef, useState } from "react";

const cacheStore = new Map(); // cacheKey -> last successful data

// Runs fetcher(signal) whenever `key` changes (or retry() is called), tracking
// loading/error state and aborting the in-flight request on unmount or a new
// key. `cacheKey` (optional) persists the latest successful result under that
// key across remounts (e.g. navigating away and back to the same page),
// so it doesn't reshow a loading skeleton for data already fetched this
// session — leave it unset to opt out of caching entirely.
export function useCachedFetch(fetcher, { key, enabled = true, cacheKey } = {}) {
  const cached = cacheKey ? cacheStore.get(cacheKey) : undefined;
  const [data, setData] = useState(cached ?? null);
  const [loading, setLoading] = useState(enabled && cached === undefined);
  const [error, setError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    // Stale-while-revalidate: if we already have cached data for this key,
    // keep showing it and refetch silently in the background instead of
    // flashing a loading state on every remount.
    if (!cacheKey || !cacheStore.has(cacheKey)) setLoading(true);
    setError(null);
    fetcherRef.current(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (cacheKey) cacheStore.set(cacheKey, result);
        setData(result);
      })
      .catch((err) => { if (!controller.signal.aborted) setError(err); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // fetcher is intentionally excluded — captured via ref so callers don't
    // need to memoize it themselves; key/enabled/cacheKey fully describe when
    // a re-fetch should happen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, retryTick, cacheKey]);

  const retry = useCallback(() => setRetryTick((t) => t + 1), []);

  return { data, loading, error, retry };
}
